import React, { PropsWithChildren, useEffect, useState, useMemo, useCallback } from "react";
import { getI18n } from "react-i18next";
import { ThemeProvider } from "@mui/material/styles";
import { ptBR, enUS } from "@mui/material/locale";

import { ColorModeContext } from "@core/contexts/ColorModeContext";
import lightTheme from "./light";
import darkTheme from "./dark";


export const ColorModeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { language } = getI18n();

  const [mode, setMode] = useState<"light" | "dark">(() => {
    const colorModeFromLocalStorage = localStorage.getItem("colorMode") as "light" | "dark" | null;
    const isSystemPreferenceDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    return colorModeFromLocalStorage ?? (isSystemPreferenceDark ? "dark" : "light");
  });

  useEffect(() => {
    localStorage.setItem("colorMode", mode);
  }, [mode]);

  const setColorMode = useCallback(() => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  }, []);

  const theme = useMemo(() => (mode === "light" ? lightTheme :  darkTheme), [mode]);

  const locale = useMemo(() => (language === "pt-BR" ? ptBR : enUS), [language]);

  const themeWithLocale = useMemo(() => ({ ...theme, ...locale }), [theme, locale]);

  return (
    <ColorModeContext.Provider value={{ setMode: setColorMode, mode }}>
      <ThemeProvider theme={themeWithLocale}>{children}</ThemeProvider>
    </ColorModeContext.Provider>
  );
};
